import {
  dailySubmitIssues,
  nextOperationMissing,
  parseVoyageReport,
  VOYAGE_REPORT_SCHEMA_VERSION,
  type VoyageReportKind,
} from '@felix/domain';
import { parseLatitude, parseLongitude } from './noon-report.js';
import type { VoyageReportInput } from './types.js';

/**
 * Noon formu (M11b): alanlar `data` ile aynı yolda durur (`data.navigation.course`); konum metin
 * olarak girilir ("34° 45.6' N"). Doğrulama sunucudaki şemayla aynıdır (`parseVoyageReport`);
 * sunucu yine kendisi doğrular (sıra, gelecek tarih, kilit).
 */
export interface NoonFormValues {
  reportedAt: string;
  tzOffsetMin: number;
  lat: string;
  lon: string;
  data: Record<string, unknown>;
}

export interface FieldError {
  field: string;
  code: string;
  /** Sıra hatasında karşılaştırılan zamanın yolu (`SchemaIssue.after` / `before`). */
  after?: string;
  before?: string;
}

const empty = (value: unknown) =>
  value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

/** Satır ya da bölümde girilmiş bir değer var mı. */
function hasValue(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(hasValue);
  if (typeof value === 'object' && value !== null) return Object.values(value).some(hasValue);
  return !empty(value);
}

/** İsteğe bağlı bölümler: tamamen boşsa gönderilmez. */
const OPTIONAL = new Set(['freshWater', 'draft']);

/**
 * Boş alanları atar. Tamamen boş dizi satırı ve isteğe bağlı bölüm gönderilmez; zorunlu bölüm
 * boş da olsa kalır ki hata alanın kendisinde görünsün.
 */
export function cleanData(value: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    if (Array.isArray(item)) {
      out[key] = item
        .filter(hasValue)
        .map((row) =>
          typeof row === 'object' && row !== null ? cleanData(row as Record<string, unknown>) : row,
        );
    } else if (typeof item === 'object' && item !== null) {
      if (OPTIONAL.has(key) && !hasValue(item)) continue;
      out[key] = cleanData(item as Record<string, unknown>);
    } else if (!empty(item)) {
      out[key] = typeof item === 'string' ? item.trim() : item;
    }
  }
  return out;
}

/** Noon alanının sekmesi (hata özeti ve ilk hatalı sekme; ortak geri bildirimi 2026-10-19). */
export function noonTabOf(path: string): 'navigation' | 'machinery' | 'fuel' | 'weather' {
  if (path.startsWith('data.machinery')) return 'machinery';
  if (path.startsWith('data.fuel')) return 'fuel';
  if (path.startsWith('data.weather')) return 'weather';
  return 'navigation';
}

export type FormConversion =
  | { ok: true; input: Omit<VoyageReportInput, 'voyageId'> }
  | { ok: false; errors: FieldError[] };

/**
 * Formdan rapor gövdesine; hatalar alan yoluyla. Taslakta zorunlu alan eksik olabilir;
 * gönderimde şemanın tamamı istenir (ADR 0022 §3).
 */
export function valuesToInput(
  values: NoonFormValues,
  options: {
    draft?: boolean;
    kind?: VoyageReportKind;
    schema?: number;
    /**
     * Defteri açık gemide yakıt sütunları gönderimde defterden yazılır (ADR 0117 §2.4): Noon'un
     * yakıt tablosu ve ROB tablosunun yakıtı formda zorunlu değildir.
     */
    fuelLedger?: boolean;
  } = {},
): FormConversion {
  const errors: FieldError[] = [];
  const lat = parseLatitude(values.lat);
  const lon = parseLongitude(values.lon);
  if (!empty(values.lat) && lat === null) errors.push({ field: 'lat', code: 'INVALID_POSITION' });
  if (!empty(values.lon) && lon === null) errors.push({ field: 'lon', code: 'INVALID_POSITION' });
  const input = {
    kind: options.kind ?? 'noon',
    reportedAt: values.reportedAt,
    tzOffsetMin: values.tzOffsetMin,
    lat,
    lon,
    data: cleanData(values.data),
  };
  // Yeni istemci şema sürümünü her zaman gönderir (ADR 0058 §1)
  const schema = options.schema ?? VOYAGE_REPORT_SCHEMA_VERSION;
  const parsed = parseVoyageReport({ ...input, schema }, { draft: options.draft ?? false });
  // Next Operation gönderimde zorunlu (ortağın cevabı #6; ADR 0057 §5)
  if (!options.draft && nextOperationMissing(input.kind, input.data)) {
    errors.push({ field: 'data.nextOperation', code: 'REQUIRED' });
  }
  // Günlüğün gönderim zorunluları: faaliyet; Port Daily'de liman (ADR 0059 §3)
  if (!options.draft) {
    for (const issue of dailySubmitIssues(input.kind, input.data)) {
      errors.push({ field: issue.path, code: issue.code });
    }
  }
  if (!parsed.ok) {
    for (const issue of parsed.issues) {
      // Biçimsiz konum zaten bildirildi
      if (errors.some((e) => e.field === issue.path)) continue;
      // Defterden yazılacak yakıt sütunlarının zorunluluğu sunucuda, gönderimde
      if (
        options.fuelLedger &&
        issue.code === 'REQUIRED' &&
        (issue.path.startsWith('data.fuel') || issue.path.startsWith('data.rob'))
      ) {
        continue;
      }
      errors.push({
        field: issue.path,
        code: issue.code,
        ...(issue.after ? { after: issue.after } : {}),
        ...(issue.before ? { before: issue.before } : {}),
      });
    }
  }
  if (errors.length > 0) return { ok: false, errors };
  if (!parsed.ok) {
    // Yalnız defterden yazılacak yakıt sütunları eksik: veri olduğu gibi gider (sunucu doldurur)
    return options.fuelLedger ? { ok: true, input: { ...input, schema } } : { ok: false, errors };
  }
  return {
    ok: true,
    input: {
      ...input,
      schema,
      data: parsed.value.data as Record<string, unknown>,
    },
  };
}
